/* eslint-disable @faire/enforce-internal-folder-encapsulation */
import { ExpectedShipDate } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ExpectedShipDate";
import { ProductName } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ProductName";
import { BrandPageProductTileSwatches } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileSwatches";
import { ProductTileInfo } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductTileInfo";
import { RetailInfoContainer } from "@faire/retailer-visitor-shared/features/MarketplaceItems/styled";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { showProductPromotions } from "@faire/retailer-visitor-shared/lib/showProductPromotions";
import { useSettingProductTileIterationInTreatment } from "@faire/retailer-visitor-shared/settings/useSettingProductTileIteration";
import { Price } from "@faire/retailer-visitor-shared/views/product/ProductDetails/Price/Price";
import { PRICE_TEXT_SURFACE } from "@faire/retailer-visitor-shared/views/product/ProductDetails/Price/PriceProps";
import { formatMoney } from "@faire/web/common/money";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import * as React from "react";

import { Caseline } from "./ComposableProductTile/__internal__/__internal__/components/CaseSizeText/Caseline";
import { OrderedCount } from "./ComposableProductTile/__internal__/__internal__/components/ProductInfo/OrderedCount";

interface IPropTypes {
  productTile: ProductTileInfo;
}

export const RetailInfo: React.FC<IPropTypes> = ({ productTile }) => {
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const showTileIterationChanges = useSettingProductTileIterationInTreatment();

  const productLevelPromotion = productTile?.min_option_wholesale_promo_price;

  const originalPrice = productTile?.min_option_wholesale_price
    ? formatMoney({
        money: productTile.min_option_wholesale_price,
        omitCentsIfZero: true,
      })
    : "";

  const wholesalePrice = productTile?.min_option_wholesale_promo_price
    ?.discounted_item_price
    ? formatMoney({
        money:
          productTile.min_option_wholesale_promo_price.discounted_item_price,
        omitCentsIfZero: true,
      })
    : originalPrice;

  const retailPrice = productTile?.min_option_retail_price
    ? formatMoney({
        money: productTile.min_option_retail_price,
        omitCentsIfZero: true,
      })
    : "";
  const { isUK } = useNationality();
  const priceComponent = (
    <Price
      isUKRetailer={isUK}
      showProductPromotions={showProductPromotions(productLevelPromotion)}
      isTilePricing
      small
      isLoggedInOrBrandPreview={isLoggedInRetailerOrBrandPreview}
      originalPrice={originalPrice}
      wholesalePrice={wholesalePrice}
      retailPrice={retailPrice}
      priceTextSurface={PRICE_TEXT_SURFACE.BRAND}
    />
  );
  return (
    <>
      <RetailInfoContainer>
        <Spacer size={showTileIterationChanges ? "1x" : 12} />
        <Column gap={showTileIterationChanges ? 4 : 0}>
          <Column gap={showTileIterationChanges ? 2 : 0}>
            {isLoggedInRetailerOrBrandPreview ? priceComponent : null}
            <ProductName productName={productTile?.product?.name ?? ""} />
            {isLoggedInRetailerOrBrandPreview ? null : priceComponent}
            {productTile && productTile.options_details ? (
              <BrandPageProductTileSwatches productTileInfo={productTile} />
            ) : null}
          </Column>
          <Column gap={showTileIterationChanges ? 2 : 0}>
            <Caseline
              isLoggedIn={isLoggedInRetailerOrBrandPreview}
              caseSizeText={productTile.case_size_text}
              availabilityText={productTile.availability_info}
            />
            <ExpectedShipDate shipDate={productTile.expected_shipping_date} />
            <OrderedCount
              orderedCountMessage={productTile.product?.order_count_message}
            />
          </Column>
        </Column>
      </RetailInfoContainer>
    </>
  );
};
